import { z } from 'zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useApi } from '../services/api';
import { Toaster, toast } from 'react-hot-toast';

interface Job {
  title: string;
  description: string;
  requirements: {
    requiredQualifications?: string[];
    requireCv: boolean;
    requirePayslip: boolean;
    requireTranscript: boolean;
    citizensOnly: boolean;
    requiresWorkAuth: boolean;
    isEmploymentEquity: boolean;
    workLocation: string;
    requiredLocation: string;
    equityRace?: string;
    equityGender?: string;
  };
}

const applicationSchema = z.object({
  cv: z.instanceof(FileList).optional(),
  payslip: z.instanceof(FileList).optional(),
  transcript: z.instanceof(FileList).optional(),
  workAuthDoc: z.instanceof(FileList).optional(),
  residentStatus: z.union([z.literal('resident'), z.literal('non-resident')]).optional(),
  applicantCitizenship: z.boolean().optional(),
  bbbeeStatus: z.boolean().optional(),
  applicantRace: z.string().optional(),
  applicantGender: z.string().optional(),
  confirmWorkLocation: z.coerce.boolean().refine((v) => v === true, { message: 'Must confirm work location' }),
});

type ApplicationFormData = z.infer<typeof applicationSchema>;

type SubmissionResult =
  | {
      status: 'PASS';
      applicationId?: string;
      message: string;
    }
  | {
      status: 'NEEDS_REVIEW';
      applicationId?: string;
      message: string;
    }
  | null;

const acceptedDocumentTypes = '.pdf,.docx,.jpg,.jpeg,.png';
const documentHelpText = 'Accepted formats: PDF, DOCX, JPG, PNG. Maximum size: 25MB.';

const ApplicationForm = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [rejectionReasons, setRejectionReasons] = useState<string[]>([]);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult>(null);

  const api = useApi();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
  });

  const residentStatus = watch('residentStatus');

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await api.get(`/jobs/public-jobs/${jobId}`);
        const requirements = response.data.requirements || {};
        setJob({
          title: response.data.title,
          description: response.data.description,
          requirements: {
            requiredQualifications: Array.isArray(requirements.requiredQualifications) ? requirements.requiredQualifications : [],
            requireCv: requirements.requireCv !== false,
            requirePayslip: Boolean(requirements.requirePayslip),
            requireTranscript: Boolean(requirements.requireTranscript),
            citizensOnly: Boolean(requirements.citizensOnly),
            requiresWorkAuth: Boolean(requirements.requiresWorkAuth),
            isEmploymentEquity: Boolean(requirements.isEmploymentEquity),
            workLocation: requirements.workLocation || 'OFFICE',
            requiredLocation: requirements.requiredLocation || '',
            equityRace: requirements.equityRace || '',
            equityGender: requirements.equityGender || '',
          },
        });
      } catch (error) {
        console.error('Fetch job error', error);
        toast.error('Failed to load job details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId, api]);

  const onSubmit: SubmitHandler<ApplicationFormData> = async (data) => {
    setSubmitLoading(true);
    setRejectionReasons([]);
    setSubmissionResult(null);
    clearErrors();

    if (!job) {
      toast.error('Job details are still loading. Please try again.');
      setSubmitLoading(false);
      return;
    }

    if (!data.confirmWorkLocation) {
      setError('confirmWorkLocation', { type: 'manual', message: 'Must confirm work location' });
      toast.error('Must confirm work location requirement');
      setSubmitLoading(false);
      return;
    }

    const hasFile = (files?: FileList) => Boolean(files && files.length > 0);
    let hasValidationError = false;

    if (job.requirements.requireCv && !hasFile(data.cv)) {
      setError('cv', { type: 'manual', message: 'CV is required for this job' });
      hasValidationError = true;
    }

    if (job.requirements.requirePayslip && !hasFile(data.payslip)) {
      setError('payslip', { type: 'manual', message: 'Payslip is required for this job' });
      hasValidationError = true;
    }

    if (job.requirements.requireTranscript && !hasFile(data.transcript)) {
      setError('transcript', { type: 'manual', message: 'Academic transcript is required for this job' });
      hasValidationError = true;
    }

    if (job.requirements.citizensOnly && data.applicantCitizenship !== true) {
      setError('applicantCitizenship', { type: 'manual', message: 'South African citizenship confirmation is required for this job' });
      hasValidationError = true;
    }

    if (!job.requirements.citizensOnly && !data.residentStatus) {
      setError('residentStatus', { type: 'manual', message: 'Resident status is required for this job' });
      hasValidationError = true;
    }

    if (
      !job.requirements.citizensOnly &&
      job.requirements.requiresWorkAuth &&
      data.residentStatus === 'non-resident' &&
      !hasFile(data.workAuthDoc)
    ) {
      setError('workAuthDoc', { type: 'manual', message: 'Work authorisation document is required for non-resident applicants' });
      hasValidationError = true;
    }

    if (job.requirements.isEmploymentEquity && job.requirements.equityRace && !data.applicantRace?.trim()) {
      setError('applicantRace', { type: 'manual', message: 'Race is required for this Employment Equity role' });
      hasValidationError = true;
    }

    if (job.requirements.isEmploymentEquity && job.requirements.equityGender && !data.applicantGender?.trim()) {
      setError('applicantGender', { type: 'manual', message: 'Gender is required for this Employment Equity role' });
      hasValidationError = true;
    }

    if (hasValidationError) {
      toast.error('Complete the required application fields before submitting.');
      setSubmitLoading(false);
      return;
    }

    const formData = new FormData();
    if (data.cv && data.cv.length > 0) formData.append('cv', data.cv.item(0)!);
    if (data.payslip && data.payslip.length > 0) formData.append('payslip', data.payslip.item(0)!);
    if (data.transcript && data.transcript.length > 0) formData.append('transcript', data.transcript.item(0)!);
    if (data.workAuthDoc && data.workAuthDoc.length > 0) formData.append('workAuthDoc', data.workAuthDoc.item(0)!);

    if (data.residentStatus) formData.append('residentStatus', data.residentStatus);
    if (data.applicantCitizenship !== undefined) formData.append('applicantCitizenship', data.applicantCitizenship.toString());
    if (data.bbbeeStatus !== undefined) formData.append('bbbeeStatus', data.bbbeeStatus.toString());

    formData.append('applicantRace', data.applicantRace || '');
    formData.append('applicantGender', data.applicantGender || '');
    formData.append('confirmWorkLocation', data.confirmWorkLocation.toString());
    formData.append('jobId', jobId || '');

    try {
      const response = await api.post('/applications', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const status = response.data.screening_status;

      if (response.data.passed_screening) {
        toast.success('Application submitted successfully!');
        setSubmissionResult({
          status: 'PASS',
          applicationId: response.data.id,
          message: 'Screening passed. Interview Recording is the next build section and is not ready yet.',
        });
        return;
      }

      if (status === 'NEEDS_REVIEW') {
        toast.success('Application submitted — pending employer review.');
        setSubmissionResult({
          status: 'NEEDS_REVIEW',
          applicationId: response.data.id,
          message:
            'Your application was submitted successfully and is pending employer review. You may be asked to provide additional information.',
        });
        return;
      }

      // FAIL
      setRejectionReasons(response.data.rejection_reasons || ['Application did not meet requirements.']);
      toast.error('Application rejected.');
    } catch (error: any) {
      console.error('Submit application error details:', error.response?.data || error.message);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading job details...</div>;
  if (!job) return <div className="text-center py-8 text-red-500">Job not found.</div>;

  const workLocationDisplay = job.requirements.requiredLocation
    ? `${job.requirements.workLocation} (${job.requirements.requiredLocation})`
    : job.requirements.workLocation;

  return (
    <div className="min-h-screen bg-surface-page text-txt-primary p-8">
      <Toaster />
      <h1 className="text-3xl font-bold mb-6">Apply for {job.title}</h1>
      <p className="mb-8">{job.description}</p>
      <div className="mb-8 rounded-input border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">Private preview</p>
        <p>
          This application form is for Jason-only smoke testing. Uploaded documents are processed by the current private screening flow,
          stored privately, and still need stronger parsing and screening-quality testing.
        </p>
      </div>

      <section className="mb-8 bg-surface-card p-6 rounded-input">
        <h2 className="text-2xl font-semibold mb-4">Job Requirements</h2>
        <ul className="list-disc pl-6">
          <li>Require CV: {job.requirements.requireCv ? 'Yes' : 'No'}</li>
          <li>Require Payslip: {job.requirements.requirePayslip ? 'Yes' : 'No'}</li>
          <li>Require Transcript: {job.requirements.requireTranscript ? 'Yes' : 'No'}</li>
          <li>Citizens Only: {job.requirements.citizensOnly ? 'Yes' : 'No'}</li>
          <li>Requires Work Auth: {job.requirements.requiresWorkAuth ? 'Yes' : 'No'}</li>
          <li>Employment Equity: {job.requirements.isEmploymentEquity ? 'Yes' : 'No'}</li>
          <li>Work Location: {workLocationDisplay}</li>
        </ul>
        {job.requirements.requiredQualifications && job.requirements.requiredQualifications.length > 0 && (
          <div className="mt-5 rounded-input border border-line bg-surface-page p-4">
            <h3 className="font-semibold text-txt-primary">Screening criteria</h3>
            <p className="mt-1 text-sm text-txt-secondary">
              Required qualifications are extracted from the CV during screening. They are not entered manually here.
            </p>
            <ul className="mt-3 list-disc pl-6">
              {job.requirements.requiredQualifications.map((qualification) => (
                <li key={qualification}>{qualification}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {job.requirements.requireCv && (
          <div>
            <label>Upload CV (required)</label>
            <input type="file" accept={acceptedDocumentTypes} {...register('cv')} />
            <p className="mt-1 text-sm text-txt-secondary">{documentHelpText}</p>
            {errors.cv?.message && <p className="text-red-400">{errors.cv.message as string}</p>}
          </div>
        )}

        {job.requirements.requirePayslip && (
          <div>
            <label>Upload Payslip</label>
            <input type="file" accept={acceptedDocumentTypes} {...register('payslip')} />
            <p className="mt-1 text-sm text-txt-secondary">{documentHelpText}</p>
            {errors.payslip?.message && <p className="text-red-400">{errors.payslip.message as string}</p>}
          </div>
        )}

        {job.requirements.requireTranscript && (
          <div>
            <label>Upload Academic Transcript</label>
            <input type="file" accept={acceptedDocumentTypes} {...register('transcript')} />
            <p className="mt-1 text-sm text-txt-secondary">{documentHelpText}</p>
            {errors.transcript?.message && <p className="text-red-400">{errors.transcript.message as string}</p>}
          </div>
        )}

        {job.requirements.citizensOnly && (
          <div>
            <input type="checkbox" {...register('applicantCitizenship')} />
            <label>I am a South African citizen (required for this job)</label>
            {errors.applicantCitizenship?.message && <p className="text-red-400">{errors.applicantCitizenship.message as string}</p>}
          </div>
        )}

        {!job.requirements.citizensOnly && (
          <div>
            <label>Resident Status</label>
            <select className="bg-white text-black p-2 rounded border border-gray-300" {...register('residentStatus')}>
              <option value="">Select</option>
              <option value="resident">Resident</option>
              <option value="non-resident">Non-Resident</option>
            </select>
            {errors.residentStatus?.message && <p className="text-red-400">{errors.residentStatus.message as string}</p>}
          </div>
        )}

        {!job.requirements.citizensOnly && job.requirements.requiresWorkAuth && residentStatus === 'non-resident' && (
          <div>
            <label>Upload Work Authorisation Document (required for non-resident applicants)</label>
            <input type="file" accept={acceptedDocumentTypes} {...register('workAuthDoc')} />
            <p className="mt-1 text-sm text-txt-secondary">{documentHelpText}</p>
            {errors.workAuthDoc?.message && <p className="text-red-400">{errors.workAuthDoc.message as string}</p>}
          </div>
        )}

        {!job.requirements.citizensOnly && job.requirements.requiresWorkAuth && residentStatus !== 'non-resident' && (
          <div className="text-sm text-txt-secondary">
            Work authorisation proof is required only if resident status is Non-Resident.
          </div>
        )}

        {job.requirements.isEmploymentEquity && (
          <div>
            <input type="checkbox" {...register('bbbeeStatus')} />
            <label>I qualify for BBBEE status (for Employment Equity)</label>
          </div>
        )}

        {job.requirements.isEmploymentEquity && (
          <div>
            <label>Race (for Employment Equity, required)</label>
            <input className="bg-white text-black p-2 rounded border border-gray-300" type="text" {...register('applicantRace')} />
            {errors.applicantRace?.message && <p className="text-red-400">{errors.applicantRace.message as string}</p>}
          </div>
        )}

        {job.requirements.isEmploymentEquity && (
          <div>
            <label>Gender (for Employment Equity, required)</label>
            <select className="bg-white text-black p-2 rounded border border-gray-300" {...register('applicantGender')}>
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.applicantGender?.message && <p className="text-red-400">{errors.applicantGender.message as string}</p>}
          </div>
        )}

        <div>
          <label>Confirm you can meet the work location requirement ({workLocationDisplay}):</label>
          <div className="flex space-x-4">
            <label>
              <input type="radio" value="true" {...register('confirmWorkLocation')} /> Yes
            </label>
            <label>
              <input type="radio" value="false" {...register('confirmWorkLocation')} /> No
            </label>
          </div>
          {errors.confirmWorkLocation?.message && <p className="text-red-400">{errors.confirmWorkLocation.message as string}</p>}
        </div>

        <button
          type="submit"
          disabled={submitLoading}
          className="bg-brand-600 hover:bg-brand-500 text-txt-primary px-6 py-2 rounded-input font-medium transition-colors"
        >
          {submitLoading ? 'Submitting...' : 'Submit Application'}
        </button>
      </form>

      {submissionResult?.status === 'PASS' && (
        <div className="mt-8 bg-green-900 p-6 rounded-input">
          <h2 className="text-2xl font-semibold mb-2 text-green-200">Screening Passed</h2>
          <p className="text-green-100">{submissionResult.message}</p>
          {submissionResult.applicationId && (
            <p className="mt-3 text-sm text-green-100">Application ID: {submissionResult.applicationId}</p>
          )}
        </div>
      )}

      {submissionResult?.status === 'NEEDS_REVIEW' && (
        <div className="mt-8 bg-blue-900 p-6 rounded-input">
          <h2 className="text-2xl font-semibold mb-2 text-blue-200">Pending Employer Review</h2>
          <p className="text-blue-100">{submissionResult.message}</p>
          {submissionResult.applicationId && (
            <p className="mt-3 text-sm text-blue-100">Application ID: {submissionResult.applicationId}</p>
          )}
        </div>
      )}

      {rejectionReasons.length > 0 && (
        <div className="mt-8 bg-red-900 p-6 rounded-input">
          <h2 className="text-2xl font-semibold mb-4 text-red-300">Application Rejected</h2>
          <ul className="list-disc pl-6 text-red-300">
            {rejectionReasons.map((reason, idx) => (
              <li key={idx}>{reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ApplicationForm;
